/**
 * Courier / mail-in service adjustment helpers (#53 pricing policy v2).
 *
 * ALL-IN TOTALS — UI shows one payable total, never a courier/repair
 * breakdown. The payable service amount is the Shopify variant price
 * (charged). A £0 variant stays £0 on the quote. adjustment still names
 * which variant the basket adds.
 *
 * Matrix (Ricky 2026-09-11):
 *   MacBook, iPhone, and iPad diagnostic (any price):
 *     collection and return included on every band, and by post
 *     B1–B4 courier → £0 · outside London → free mail-in
 *   <£200 (untagged / paid):
 *     B1–B2 → +£25 courier today, or +£20 mail-in
 *     B3/B4 / outside → mail-in +£20 only
 *
 * Theme assets: courier-london-bands.json, service-adjustment-variants.json
 * No secrets. Safe for browser (theme asset) and Node (tests).
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.ICorrectCourier = factory();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var TIER_TAGS = {
    'courier:free': 'free',
    'courier:one-leg': 'one-leg',
    /* legacy pre-v2 tags → paid unless free/one-leg also present */
    'courier:subsidised': 'paid',
    'courier:full': 'paid',
  };

  var ADJUSTMENT = {
    B3_ONE_LEG: 15,
    B4_ONE_LEG: 25,
    PAID_COURIER: 25,
    MAIL_IN: 20,
  };

  function roundMoney(value) {
    return Math.round(Number(value) * 100) / 100;
  }

  /* UK outward code: A9, A99, AA9, AA99, A9A, AA9A. Inward code: 9AA. */
  var OUTWARD_RE = /^(?:[A-Z]{1,2}[0-9]{1,2}|[A-Z]{1,2}[0-9][A-Z])$/;
  var INWARD_RE = /^[0-9][A-Z]{2}$/;

  /**
   * A complete UK postcode (outward + inward), with or without a space,
   * in any case. Returns null while the postcode is partial or invalid,
   * e.g. "W1W", "W1W 8", "W1W 8J", "W1W8J". An inward code always ends in
   * two letters and an outward code never does, so a half-typed postcode
   * can never pass.
   * @param {string} postcode
   * @returns {{ outward: string, inward: string, formatted: string }|null}
   */
  function parseFullPostcode(postcode) {
    if (postcode == null) return null;
    var compact = String(postcode).toUpperCase().replace(/\s+/g, '');
    if (compact.length < 5 || compact.length > 7) return null;
    var outward = compact.slice(0, -3);
    var inward = compact.slice(-3);
    if (!OUTWARD_RE.test(outward) || !INWARD_RE.test(inward)) return null;
    return { outward: outward, inward: inward, formatted: outward + ' ' + inward };
  }

  function isFullPostcode(postcode) {
    return parseFullPostcode(postcode) !== null;
  }

  /**
   * Outward code of a UK postcode, uppercased, with or without a space.
   * Full postcode: whitespace removed, the outward code is everything
   * except the last 3 characters (W1F0DP, w1f 0dp -> W1F; SW114GG -> SW11;
   * EC1A1BB -> EC1A). Both halves must match the UK format.
   * Outward code alone, or a half-typed inward after a space, still
   * returns the outward code (SW11, "SW11 4" -> SW11).
   * Anything else returns null (caller falls back to mail-in).
   * @param {string} postcode
   * @returns {string|null}
   */
  function extractOutwardCode(postcode) {
    if (postcode == null) return null;
    var upper = String(postcode).toUpperCase().trim();
    if (!upper) return null;
    var full = parseFullPostcode(upper);
    if (full) return full.outward;
    var first = upper.split(/\s+/)[0];
    if (OUTWARD_RE.test(first)) return first;
    return null;
  }

  /**
   * @param {string} postcode
   * @param {{ outward: Record<string, { band: string, rt_cost: number }> }} bandsAsset
   * @returns {{ service: 'courier'|'mail-in', outward: string|null, band: string|null, rt_cost: number|null }}
   */
  function lookupCourierBand(postcode, bandsAsset) {
    var outward = extractOutwardCode(postcode);
    if (!outward || !bandsAsset || !bandsAsset.outward) {
      return { service: 'mail-in', outward: outward, band: null, rt_cost: null };
    }
    var entry = bandsAsset.outward[outward];
    /* District fallback: SW1A → SW1 when only the district key exists. */
    if (!entry && /[A-Z]$/.test(outward)) {
      var district = outward.slice(0, -1);
      if (bandsAsset.outward[district]) {
        entry = bandsAsset.outward[district];
      }
    }
    if (!entry) {
      return { service: 'mail-in', outward: outward, band: null, rt_cost: null };
    }
    return {
      service: 'courier',
      outward: outward,
      band: entry.band,
      rt_cost: entry.rt_cost,
    };
  }

  function normalizeTags(productTags) {
    return Array.isArray(productTags)
      ? productTags
      : String(productTags || '')
          .split(',')
          .map(function (t) {
            return t.trim();
          })
          .filter(Boolean);
  }

  /**
   * Website cart label for Monday Repair Type (status24).
   * Diagnostic when the quoted SKU / wizard route is a diagnostic; otherwise Repair.
   * Draft orders never go through this helper.
   * @param {{ device?: string, repairType?: string, route?: string, title?: string, handle?: string, productType?: string, productTitle?: string, productHandle?: string }} [opts]
   * @returns {'Diagnostic'|'Repair'}
   */
  function repairTypeCartLabel(opts) {
    return isDiagnosticRepair(opts) ? 'Diagnostic' : 'Repair';
  }

  /**
   * True when the quoted product/route is a diagnostic (any device).
   * @param {{ device?: string, repairType?: string, route?: string, title?: string, handle?: string, productType?: string, productTitle?: string, productHandle?: string }} [opts]
   */
  function isDiagnosticRepair(opts) {
    opts = opts || {};
    var repairType = String(opts.repairType || opts.route || '').toLowerCase();
    var title = String(opts.title || opts.productTitle || '');
    var handle = String(opts.handle || opts.productHandle || '');
    var blob = title + ' ' + handle;
    return repairType === 'diagnostic' || /\bdiagnostic\b/i.test(blob);
  }

  /**
   * Device family for a diagnostic SKU. Title and handle cover product pages
   * that do not pass the wizard device field.
   * @param {{ device?: string, repairType?: string, route?: string, title?: string, handle?: string, productType?: string, productTitle?: string, productHandle?: string }} [opts]
   * @returns {'macbook'|'iphone'|'ipad'|null}
   */
  function diagnosticFamily(opts) {
    if (!isDiagnosticRepair(opts)) return null;
    opts = opts || {};
    var device = String(opts.device || opts.productType || '').toLowerCase();
    var blob = (
      String(opts.title || opts.productTitle || '') +
      ' ' +
      String(opts.handle || opts.productHandle || '')
    ).toLowerCase();
    if (device === 'macbook' || device === 'mac' || /macbook/.test(device) || /macbook/.test(blob)) {
      return 'macbook';
    }
    if (device === 'iphone' || /iphone/.test(device) || /iphone/.test(blob)) return 'iphone';
    if (device === 'ipad' || /ipad/.test(device) || /ipad/.test(blob)) return 'ipad';
    return null;
  }

  /**
   * MacBook diagnostic includes collection and return on every band.
   * @param {{ device?: string, repairType?: string, title?: string, handle?: string, productType?: string, productTitle?: string, productHandle?: string }} [opts]
   */
  function isMacbookDiagnostic(opts) {
    return diagnosticFamily(opts) === 'macbook';
  }

  /**
   * iPhone and iPad diagnostics include collection and return on every band.
   * @param {{ device?: string, repairType?: string, title?: string, handle?: string, productType?: string, productTitle?: string, productHandle?: string }} [opts]
   */
  function isHandheldDiagnostic(opts) {
    var family = diagnosticFamily(opts);
    return family === 'iphone' || family === 'ipad';
  }

  /**
  /**
   * Tag beats price threshold. included diagnostic > free > one-leg > paid.
   * MacBook, iPhone, and iPad diagnostics include collection on every band.
   * @param {string[]|string} productTags
   * @param {number} [repairPrice] optional — ≥£200 → free when untagged
   * @param {object} [opts]
   * @returns {'included'|'free'|'one-leg'|'paid'}
   */
  function resolveCourierTier(productTags, repairPrice, opts) {
    if (isHandheldDiagnostic(opts) || isMacbookDiagnostic(opts)) return 'included';
    var tags = normalizeTags(productTags);
    var found = null;
    var rank = { free: 3, 'one-leg': 2, paid: 1 };
    for (var i = 0; i < tags.length; i++) {
      var tier = TIER_TAGS[String(tags[i]).toLowerCase()];
      if (tier && (!found || rank[tier] > rank[found])) {
        found = tier;
      }
    }
    if (found === 'free' || found === 'one-leg') return found;
    if (found === 'paid') return 'paid';
    if (Number.isFinite(Number(repairPrice)) && Number(repairPrice) >= 200) {
      return 'free';
    }
    return 'paid';
  }

  function isFreeEligible(tier) {
    return tier === 'free' || tier === 'one-leg' || tier === 'included';
  }

  /**
   * @param {'free'|'one-leg'|'paid'} tier
   * @param {string|null} band B1–B4 or null (outside London)
   * @param {'courier'|'mail-in'} service
   * @returns {{ available: boolean, adjustment: number|null }}
   */
  function computeAdjustment(tier, band, service) {
    /* iPhone/iPad diagnostic: courier is £0 on every London band, post is £0. */
    if (tier === 'included') {
      if (service === 'mail-in') return { available: true, adjustment: 0 };
      if (band === 'B1' || band === 'B2' || band === 'B3' || band === 'B4') {
        return { available: true, adjustment: 0 };
      }
      return { available: false, adjustment: null };
    }

    var free = isFreeEligible(tier);

    if (service === 'mail-in') {
      return { available: true, adjustment: free ? 0 : ADJUSTMENT.MAIL_IN };
    }

    if (!band) {
      return { available: false, adjustment: null };
    }
    if (free) {
      if (band === 'B1' || band === 'B2') {
        return { available: true, adjustment: 0 };
      }
      if (band === 'B3') {
        return { available: true, adjustment: ADJUSTMENT.B3_ONE_LEG };
      }
      if (band === 'B4') {
        return { available: true, adjustment: ADJUSTMENT.B4_ONE_LEG };
      }
      return { available: false, adjustment: null };
    }

    /* paid: courier only B1–B2 */
    if (band === 'B1' || band === 'B2') {
      return { available: true, adjustment: ADJUSTMENT.PAID_COURIER };
    }
    return { available: false, adjustment: null };
  }

  /**
   * Resolve Shopify variant ID for a quoted adjustment.
   * Null-safe: missing ID → free mail-in fallback (never charge £0 for a quote).
   *
   * @param {number} adjustment
   * @param {{ variants?: Record<string, number> }|Record<string, number>|null} variantsAsset
   * @param {{ warn?: function }} [opts]
   * @returns {{ variantId: number|null, mode: 'none'|'ok'|'fallback-free-mail-in', adjustment: number }}
   */
  function resolveAdjustmentVariant(adjustment, variantsAsset, opts) {
    var amount = roundMoney(adjustment || 0);
    if (!amount || amount <= 0) {
      return { variantId: null, mode: 'none', adjustment: 0 };
    }
    /* No variants asset yet (tests / early UI) — keep quoted adjustment, unresolved. */
    if (variantsAsset == null) {
      return { variantId: null, mode: 'unresolved', adjustment: amount };
    }
    var map =
      variantsAsset.variants != null ? variantsAsset.variants : variantsAsset;
    var key = String(Math.round(amount));
    var raw = map ? map[key] : null;
    var vid = raw != null && raw !== '' ? Number(raw) : null;
    if (vid && Number.isFinite(vid) && vid > 0) {
      return { variantId: vid, mode: 'ok', adjustment: amount };
    }
    var warn = opts && opts.warn ? opts.warn : console.warn;
    try {
      warn(
        '[icorrect courier] missing service-adjustment variant for £' +
          key +
          ' — falling back to free mail-in (never block checkout, never misprice)'
      );
    } catch (e) {}
    return { variantId: null, mode: 'fallback-free-mail-in', adjustment: 0 };
  }

  /**
   * Pounds the basket will add for this policy amount.
   * prices[key] is the Shopify variant price. Missing price → policy amount.
   * @param {number} adjustment
   * @param {{ prices?: Record<string, number> }|null} variantsAsset
   */
  function payableCharge(adjustment, variantsAsset) {
    var amount = roundMoney(adjustment || 0);
    if (!(amount > 0) || !variantsAsset || !variantsAsset.prices) return amount;
    var key = String(Math.round(amount));
    var raw = variantsAsset.prices[key];
    if (raw == null || raw === '') return amount;
    var shelf = Number(raw);
    if (!Number.isFinite(shelf) || shelf < 0) return amount;
    return roundMoney(shelf);
  }

  /**
   * Copy Ajax /products/service-adjustment.js prices (integer pence) onto
   * variantsAsset.prices, keyed by the policy amount ("15", "20", "25").
   * @param {{ variants?: Record<string, number>, prices?: Record<string, number> }|null} variantsAsset
   * @param {{ variants?: Array<{ id: number, price: number }> }|null} product
   */
  function applyShelfPrices(variantsAsset, product) {
    if (!variantsAsset || !product || !Array.isArray(product.variants)) return variantsAsset;
    var byId = {};
    for (var i = 0; i < product.variants.length; i++) {
      var variant = product.variants[i];
      if (!variant || variant.id == null) continue;
      var cents = Number(variant.price);
      if (!Number.isFinite(cents)) continue;
      byId[String(variant.id)] = roundMoney(cents / 100);
    }
    var map = variantsAsset.variants || {};
    if (!variantsAsset.prices || typeof variantsAsset.prices !== 'object') {
      variantsAsset.prices = {};
    }
    Object.keys(map).forEach(function (key) {
      var id = String(map[key]);
      if (Object.prototype.hasOwnProperty.call(byId, id)) {
        variantsAsset.prices[key] = byId[id];
      }
    });
    return variantsAsset;
  }

  /**
   * Full quote for the selected service preference.
   *
   * @param {{
   *   postcode: string,
   *   productTags?: string[]|string,
   *   bands: object,
   *   service?: 'courier'|'mail-in',
   *   repairPrice?: number,
   *   variants?: object
   * }} opts
   */
  function quoteServiceAdjustment(opts) {
    opts = opts || {};
    var preference = opts.service === 'mail-in' ? 'mail-in' : 'courier';
    var lookup = lookupCourierBand(opts.postcode, opts.bands);
    var tier = resolveCourierTier(opts.productTags, opts.repairPrice, opts);
    var band = lookup.band;
    var repair = Number.isFinite(Number(opts.repairPrice))
      ? roundMoney(opts.repairPrice)
      : null;

    var chosen = preference;
    var computed = computeAdjustment(tier, band, chosen);
    var forcedMailIn = false;

    if (chosen === 'courier' && !computed.available) {
      forcedMailIn = true;
      chosen = 'mail-in';
      computed = computeAdjustment(tier, band, 'mail-in');
    }

    if (preference === 'courier' && lookup.service === 'mail-in' && !forcedMailIn) {
      forcedMailIn = true;
      chosen = 'mail-in';
      computed = computeAdjustment(tier, null, 'mail-in');
    }

    var adjustment = computed.adjustment != null ? roundMoney(computed.adjustment) : 0;
    var variantInfo = resolveAdjustmentVariant(adjustment, opts.variants || null);

    if (variantInfo.mode === 'fallback-free-mail-in') {
      forcedMailIn = true;
      chosen = 'mail-in';
      adjustment = 0;
    }

    /* Basket line price. £0 variants must not inflate the wizard total. */
    var charged = payableCharge(adjustment, opts.variants || null);
    var total = repair != null ? roundMoney(repair + charged) : null;

    return {
      service: chosen,
      preference: preference,
      courierAvailable:
        lookup.service === 'courier' &&
        computeAdjustment(tier, band, 'courier').available,
      outward: lookup.outward,
      band: lookup.service === 'courier' ? band : null,
      rt_cost: lookup.service === 'courier' ? lookup.rt_cost : null,
      tier: tier,
      adjustment: adjustment,
      /* Pounds added at checkout. Equals adjustment unless the variant is priced differently. */
      charged: charged,
      customer_price: charged,
      repair_price: repair,
      total: total,
      forcedMailIn: forcedMailIn,
      variantId: variantInfo.variantId,
      variantMode: variantInfo.mode,
    };
  }

  /**
   * Quote for courier preference (auto-falls back to mail-in when unavailable).
   * @param {{ postcode: string, productTags: string[]|string, bands: object, repairPrice?: number, variants?: object }} opts
   */
  function quoteCourierCollection(opts) {
    return quoteServiceAdjustment(
      Object.assign({}, opts, { service: 'courier' })
    );
  }

  /**
   * @deprecated policy v2 — adjustment for courier by tier+band (rtCost ignored).
   */
  function customerCourierPrice(tier, rtCost, band) {
    var computed = computeAdjustment(tier, band || null, 'courier');
    if (!computed.available) return null;
    return computed.adjustment;
  }

  var FUNNEL_PII_KEYS = {
    postcode: true,
    Postcode: true,
    full_postcode: true,
    postal_code: true,
    email: true,
    name: true,
    phone: true,
    address: true,
  };

  /**
   * PostHog courier-funnel extra fields. Outward code only — never a full postcode.
   * @param {object|null} quote quoteServiceAdjustment result
   * @param {object} [extra]
   */
  function courierFunnelExtra(quote, extra) {
    quote = quote || {};
    var payload = {
      outward: quote.outward || null,
      band: quote.band == null ? null : quote.band,
      tier: quote.tier || null,
      adjustment: quote.adjustment == null ? null : quote.adjustment,
      repair_price: quote.repair_price == null ? null : quote.repair_price,
      total: quote.total == null ? null : quote.total,
      service: quote.service || null,
    };
    if (extra) {
      Object.keys(extra).forEach(function (key) {
        if (extra[key] === undefined) return;
        if (FUNNEL_PII_KEYS[key]) return;
        payload[key] = extra[key];
      });
    }
    return payload;
  }

  return {
    TIER_TAGS: TIER_TAGS,
    ADJUSTMENT: ADJUSTMENT,
    extractOutwardCode: extractOutwardCode,
    parseFullPostcode: parseFullPostcode,
    isFullPostcode: isFullPostcode,
    lookupCourierBand: lookupCourierBand,
    isMacbookDiagnostic: isMacbookDiagnostic,
    isHandheldDiagnostic: isHandheldDiagnostic,
    diagnosticFamily: diagnosticFamily,
    isDiagnosticRepair: isDiagnosticRepair,
    repairTypeCartLabel: repairTypeCartLabel,
    resolveCourierTier: resolveCourierTier,
    computeAdjustment: computeAdjustment,
    resolveAdjustmentVariant: resolveAdjustmentVariant,
    payableCharge: payableCharge,
    applyShelfPrices: applyShelfPrices,
    quoteServiceAdjustment: quoteServiceAdjustment,
    quoteCourierCollection: quoteCourierCollection,
    customerCourierPrice: customerCourierPrice,
    courierFunnelExtra: courierFunnelExtra,
    isFreeEligible: isFreeEligible,
    roundMoney: roundMoney,
  };
});
